import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { 
  Users, Briefcase, Film, Award, Calendar, TrendingUp, 
  Settings, LogOut, Bell, Search, Filter, Download, 
  Eye, Trash2, CheckCircle, XCircle, Clock, Mail, Phone,
  User, Shield, Plus, Edit, Save, X, GripVertical, FileText,
  BarChart, PieChart, Activity, AlertCircle, ChevronDown, ChevronUp,
  Menu, Home, Folder, ChevronLeft, ChevronRight
} from 'lucide-react'
import toast from 'react-hot-toast'

const AdminDashboard = () => {
  const navigate = useNavigate()
  const { logout, currentAdmin, hasPermission, adminRole } = useAuth()
  const [activeTab, setActiveTab] = useState('overview')
  const [applications, setApplications] = useState([])
  const [notifications, setNotifications] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [selectedApplication, setSelectedApplication] = useState(null)
  const [showNotifications, setShowNotifications] = useState(false)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)

  // Settings state
  const [customFields, setCustomFields] = useState([])
  const [userRoles, setUserRoles] = useState([])
  const [editingField, setEditingField] = useState(null)
  const [editingRole, setEditingRole] = useState(null)
  const [showFieldForm, setShowFieldForm] = useState(false)
  const [showRoleForm, setShowRoleForm] = useState(false)
  const [activeSettingsTab, setActiveSettingsTab] = useState('custom-fields')

  const fieldTypes = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'select', label: 'Select Dropdown' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'image', label: 'Image Upload' }
  ]

  const defaultRoles = [
    {
      id: 'super-admin',
      name: 'Super Admin',
      description: 'Full system access with all permissions',
      permissions: {
        canManageAdmins: true,
        canManageApplications: true,
        canUpdateStatus: true,
        canDelete: true,
        canExport: true,
        canViewAnalytics: true,
        canManageContent: true,
        canManageSettings: true
      },
      isDefault: true
    },
    {
      id: 'admin',
      name: 'Admin',
      description: 'Can manage applications and content',
      permissions: {
        canManageAdmins: false,
        canManageApplications: true,
        canUpdateStatus: true,
        canDelete: true,
        canExport: true,
        canViewAnalytics: true,
        canManageContent: true,
        canManageSettings: false
      },
      isDefault: true
    },
    {
      id: 'editor',
      name: 'Editor',
      description: 'Can view and edit applications',
      permissions: {
        canManageAdmins: false,
        canManageApplications: true,
        canUpdateStatus: false,
        canDelete: false,
        canExport: true,
        canViewAnalytics: true,
        canManageContent: true,
        canManageSettings: false
      },
      isDefault: true
    },
    {
      id: 'viewer',
      name: 'Viewer',
      description: 'Read-only access',
      permissions: {
        canManageAdmins: false,
        canManageApplications: false,
        canUpdateStatus: false,
        canDelete: false,
        canExport: false,
        canViewAnalytics: true,
        canManageContent: false,
        canManageSettings: false
      },
      isDefault: true
    }
  ]

  const menuItems = [
    { id: 'overview', label: 'Overview', icon: Home },
    { id: 'applications', label: 'Applications', icon: Briefcase },
    { id: 'settings', label: 'Settings', icon: Settings },
  ]

  useEffect(() => {
    loadApplications()
    loadNotifications()
    loadCustomFields()
    loadUserRoles()
  }, [])

  const loadApplications = () => {
    const jobApps = JSON.parse(localStorage.getItem('shikdar_films_job_applications') || '[]')
    const castingApps = JSON.parse(localStorage.getItem('shikdar_films_applications') || '[]')
    const allApps = [...jobApps, ...castingApps].map(app => ({
      ...app,
      type: app.position ? 'job' : 'casting'
    }))
    setApplications(allApps)
  }

  const loadNotifications = () => {
    const saved = JSON.parse(localStorage.getItem('shikdar_films_notifications') || '[]')
    setNotifications(saved.filter(n => !n.read).slice(0, 5))
  }

  const loadCustomFields = () => {
    const saved = JSON.parse(localStorage.getItem('shikdar_films_custom_fields') || '[]')
    setCustomFields(saved)
  }

  const loadUserRoles = () => {
    const saved = JSON.parse(localStorage.getItem('shikdar_films_user_roles') || '[]')
    const allRoles = [...defaultRoles, ...saved]
    setUserRoles(allRoles)
  }

  const handleLogout = () => {
    logout()
    navigate('/admin/login')
    toast.success('Logged out successfully')
  }

  const updateApplicationStatus = (id, newStatus) => {
    if (!hasPermission('canUpdateStatus')) {
      toast.error('You do not have permission to update status')
      return
    }

    const jobApps = JSON.parse(localStorage.getItem('shikdar_films_job_applications') || '[]')
    const castingApps = JSON.parse(localStorage.getItem('shikdar_films_applications') || '[]')
    
    const updatedJobApps = jobApps.map(app => 
      app.id === id ? { ...app, status: newStatus } : app
    )
    const updatedCastingApps = castingApps.map(app => 
      app.id === id ? { ...app, status: newStatus } : app
    )
    
    localStorage.setItem('shikdar_films_job_applications', JSON.stringify(updatedJobApps))
    localStorage.setItem('shikdar_films_applications', JSON.stringify(updatedCastingApps))
    
    loadApplications()
    toast.success('Status updated successfully')
  }

  const deleteApplication = (id) => {
    if (!hasPermission('canDelete')) {
      toast.error('You do not have permission to delete')
      return
    }

    if (!confirm('Are you sure you want to delete this application?')) return

    const jobApps = JSON.parse(localStorage.getItem('shikdar_films_job_applications') || '[]')
    const castingApps = JSON.parse(localStorage.getItem('shikdar_films_applications') || '[]')
    
    const updatedJobApps = jobApps.filter(app => app.id !== id)
    const updatedCastingApps = castingApps.filter(app => app.id !== id)
    
    localStorage.setItem('shikdar_films_job_applications', JSON.stringify(updatedJobApps))
    localStorage.setItem('shikdar_films_applications', JSON.stringify(updatedCastingApps))
    
    loadApplications()
    toast.success('Application deleted')
  }

  const exportApplications = () => {
    if (!hasPermission('canExport')) {
      toast.error('You do not have permission to export')
      return
    }

    const dataStr = JSON.stringify(applications, null, 2)
    const blob = new Blob([dataStr], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `applications_${new Date().toISOString().split('T')[0]}.json`
    a.click()
    URL.revokeObjectURL(url)
    toast.success('Applications exported!')
  }

  const saveCustomField = (fieldData) => {
    if (editingField) {
      const updated = customFields.map(f => f.id === editingField.id ? { ...fieldData, id: editingField.id } : f)
      setCustomFields(updated)
      localStorage.setItem('shikdar_films_custom_fields', JSON.stringify(updated))
      toast.success('Custom field updated!')
    } else {
      const newField = { ...fieldData, id: Date.now() }
      const updated = [...customFields, newField]
      setCustomFields(updated)
      localStorage.setItem('shikdar_films_custom_fields', JSON.stringify(updated))
      toast.success('Custom field created!')
    }
    setShowFieldForm(false)
    setEditingField(null)
  }

  const deleteCustomField = (id) => {
    if (!confirm('Delete this custom field?')) return
    const updated = customFields.filter(f => f.id !== id)
    setCustomFields(updated)
    localStorage.setItem('shikdar_films_custom_fields', JSON.stringify(updated))
    toast.success('Custom field deleted')
  }

  const moveField = (index, direction) => {
    const newFields = [...customFields]
    const targetIndex = direction === 'up' ? index - 1 : index + 1
    if (targetIndex >= 0 && targetIndex < customFields.length) {
      [newFields[index], newFields[targetIndex]] = [newFields[targetIndex], newFields[index]]
      setCustomFields(newFields)
      localStorage.setItem('shikdar_films_custom_fields', JSON.stringify(newFields))
    }
  }

  const saveUserRole = (roleData) => {
    if (editingRole) {
      if (editingRole.isDefault) {
        toast.error('Cannot modify default roles')
        return
      }
      const customRoles = userRoles.filter(r => !r.isDefault)
      const updated = customRoles.map(r => r.id === editingRole.id ? { ...roleData, id: editingRole.id } : r)
      setUserRoles([...defaultRoles, ...updated])
      localStorage.setItem('shikdar_films_user_roles', JSON.stringify(updated))
      toast.success('User role updated!')
    } else {
      const newRole = { ...roleData, id: Date.now().toString(), isDefault: false }
      const customRoles = userRoles.filter(r => !r.isDefault)
      const updated = [...customRoles, newRole]
      setUserRoles([...defaultRoles, ...updated])
      localStorage.setItem('shikdar_films_user_roles', JSON.stringify(updated))
      toast.success('User role created!')
    }
    setShowRoleForm(false)
    setEditingRole(null)
  }

  const deleteUserRole = (id) => {
    const role = userRoles.find(r => r.id === id)
    if (role?.isDefault) {
      toast.error('Cannot delete default roles')
      return
    }
    if (!confirm('Delete this user role?')) return
    
    const customRoles = userRoles.filter(r => !r.isDefault && r.id !== id)
    setUserRoles([...defaultRoles, ...customRoles])
    localStorage.setItem('shikdar_films_user_roles', JSON.stringify(customRoles))
    toast.success('User role deleted')
  }

  const filteredApplications = applications.filter(app => {
    const matchesSearch = app.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         app.email?.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesFilter = filterStatus === 'all' || app.status === filterStatus
    return matchesSearch && matchesFilter
  })

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      reviewed: 'bg-blue-100 text-blue-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const stats = {
    total: applications.length,
    pending: applications.filter(a => a.status === 'pending').length,
    reviewed: applications.filter(a => a.status === 'reviewed').length,
    accepted: applications.filter(a => a.status === 'accepted').length
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Left Sidebar */}
      <aside className={`fixed lg:sticky top-0 left-0 h-screen bg-white border-r border-gray-200 z-50 transition-all duration-300 ${
        sidebarCollapsed ? 'w-20' : 'w-64'
      } ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        
        {/* Sidebar Header */}
        <div className="h-20 flex items-center justify-between px-4 border-b border-gray-200">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-bengal-green to-bengal-orange rounded-lg flex items-center justify-center">
                <Film className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-gray-900 section-title">Admin Panel</h1>
                <p className="text-xs text-gray-600">{currentAdmin}</p>
              </div>
            </div>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="hidden lg:flex p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {sidebarCollapsed ? <ChevronRight className="w-5 h-5" /> : <ChevronLeft className="w-5 h-5" />}
          </button>
          <button
            onClick={() => setMobileSidebarOpen(false)}
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User Profile Section */}
        {!sidebarCollapsed && (
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 bg-bengal-green rounded-full flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 truncate">{currentAdmin}</p>
                <span className="inline-block px-2 py-0.5 bg-purple-100 text-purple-800 text-xs rounded-full capitalize">
                  {adminRole?.replace('-', ' ')}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Navigation Menu */}
        <nav className="flex-1 overflow-y-auto p-4">
          <div className="space-y-1">
            {menuItems.map((item) => {
              const Icon = item.icon
              const isActive = activeTab === item.id
              return (
                <button
                  key={item.id}
                  onClick={() => {
                    setActiveTab(item.id)
                    setMobileSidebarOpen(false)
                  }}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                    isActive
                      ? 'bg-bengal-green text-white shadow-md'
                      : 'text-gray-700 hover:bg-gray-100'
                  } ${sidebarCollapsed ? 'justify-center' : ''}`}
                  title={sidebarCollapsed ? item.label : ''}
                >
                  <Icon className="w-5 h-5 flex-shrink-0" />
                  {!sidebarCollapsed && <span className="font-medium">{item.label}</span>}
                </button>
              )
            })}
          </div>

          {/* Quick Actions */}
          {!sidebarCollapsed && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-xs font-semibold text-gray-500 uppercase mb-3 px-4">Quick Actions</p>
              <div className="space-y-1">
                <button
                  onClick={() => navigate('/admin/profile')}
                  className="w-full flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <User className="w-4 h-4" />
                  <span className="text-sm">My Profile</span>
                </button>
                {hasPermission('canManageAdmins') && (
                  <button
                    onClick={() => navigate('/admin/register')}
                    className="w-full flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="text-sm">Add Admin</span>
                  </button>
                )}
                <button
                  onClick={exportApplications}
                  className="w-full flex items-center gap-3 px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Download className="w-4 h-4" />
                  <span className="text-sm">Export Data</span>
                </button>
              </div>
            </div>
          )}
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors ${
              sidebarCollapsed ? 'justify-center' : ''
            }`}
            title={sidebarCollapsed ? 'Logout' : ''}
          >
            <LogOut className="w-5 h-5" />
            {!sidebarCollapsed && <span className="font-medium">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setMobileSidebarOpen(true)}
                  className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Menu className="w-6 h-6 text-gray-700" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900 section-title">
                    {menuItems.find(item => item.id === activeTab)?.label || 'Dashboard'}
                  </h1>
                  <p className="text-sm text-gray-600">Welcome back, {currentAdmin}!</p>
                </div>
              </div>

              {/* Notifications */}
              <div className="relative">
                <button
                  onClick={() => setShowNotifications(!showNotifications)}
                  className="relative p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <Bell className="w-6 h-6 text-gray-700" />
                  {notifications.length > 0 && (
                    <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
                  )}
                </button>

                {showNotifications && (
                  <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 max-h-96 overflow-y-auto">
                    <div className="p-4 border-b border-gray-200">
                      <h3 className="font-semibold text-gray-900">Notifications</h3>
                    </div>
                    {notifications.length > 0 ? (
                      <div className="divide-y divide-gray-200">
                        {notifications.map(notif => (
                          <div key={notif.id} className="p-4 hover:bg-gray-50">
                            <p className="font-medium text-gray-900 text-sm">{notif.title}</p>
                            <p className="text-sm text-gray-600 mt-1">{notif.message}</p>
                            <p className="text-xs text-gray-500 mt-2">{formatDate(notif.timestamp)}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="p-8 text-center">
                        <Bell className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-gray-500">No new notifications</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Stats Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
                  <div className="flex items-center justify-between mb-2">
                    <Briefcase className="w-8 h-8 text-blue-600" />
                    <span className="text-2xl font-bold text-blue-900">{stats.total}</span>
                  </div>
                  <p className="text-sm text-blue-700 font-medium">Total Applications</p>
                </div>

                <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-yellow-500">
                  <div className="flex items-center justify-between mb-2">
                    <Clock className="w-8 h-8 text-yellow-600" />
                    <span className="text-2xl font-bold text-yellow-900">{stats.pending}</span>
                  </div>
                  <p className="text-sm text-yellow-700 font-medium">Pending Review</p>
                </div>

                <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500">
                  <div className="flex items-center justify-between mb-2">
                    <Eye className="w-8 h-8 text-purple-600" />
                    <span className="text-2xl font-bold text-purple-900">{stats.reviewed}</span>
                  </div>
                  <p className="text-sm text-purple-700 font-medium">Reviewed</p>
                </div>

                <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
                  <div className="flex items-center justify-between mb-2">
                    <CheckCircle className="w-8 h-8 text-green-600" />
                    <span className="text-2xl font-bold text-green-900">{stats.accepted}</span>
                  </div>
                  <p className="text-sm text-green-700 font-medium">Accepted</p>
                </div>
              </div>

              {/* Recent Activity */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                  <Activity className="w-5 h-5 text-bengal-green" />
                  Recent Activity
                </h3>
                <div className="space-y-3">
                  {applications.slice(0, 5).map(app => (
                    <div key={app.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-bengal-green rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-white" />
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{app.name}</p>
                          <p className="text-sm text-gray-600">{app.email}</p>
                        </div>
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(app.status)}`}>
                        {app.status}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Applications Tab */}
          {activeTab === 'applications' && (
            <div className="space-y-6">
              {/* Search and Filters */}
              <div className="bg-white rounded-xl shadow-md p-6">
                <div className="flex flex-col md:flex-row gap-4">
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search applications..."
                      className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                    />
                  </div>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-bengal-green focus:border-transparent"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="reviewed">Reviewed</option>
                    <option value="accepted">Accepted</option>
                    <option value="rejected">Rejected</option>
                  </select>
                </div>
              </div>

              {/* Applications Table */}
              <div className="bg-white rounded-xl shadow-md overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applicant</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {filteredApplications.map(app => (
                        <tr key={app.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            <div>
                              <p className="font-medium text-gray-900">{app.name}</p>
                              <p className="text-sm text-gray-600">{app.email}</p>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full capitalize">
                              {app.type}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <select
                              value={app.status}
                              onChange={(e) => updateApplicationStatus(app.id, e.target.value)}
                              className={`px-3 py-1 rounded-full text-xs font-semibold border-0 ${getStatusColor(app.status)}`}
                              disabled={!hasPermission('canUpdateStatus')}
                            >
                              <option value="pending">Pending</option>
                              <option value="reviewed">Reviewed</option>
                              <option value="accepted">Accepted</option>
                              <option value="rejected">Rejected</option>
                            </select>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-600">
                            {formatDate(app.timestamp)}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => setSelectedApplication(app)}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                                title="View"
                              >
                                <Eye className="w-4 h-4" />
                              </button>
                              {hasPermission('canDelete') && (
                                <button
                                  onClick={() => deleteApplication(app.id)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="space-y-6">
              {/* Settings Navigation */}
              <div className="bg-white rounded-xl shadow-md p-2">
                <div className="flex gap-2">
                  <button
                    onClick={() => setActiveSettingsTab('custom-fields')}
                    className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                      activeSettingsTab === 'custom-fields'
                        ? 'bg-bengal-green text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    Custom Fields
                  </button>
                  <button
                    onClick={() => setActiveSettingsTab('user-roles')}
                    className={`flex-1 px-4 py-3 rounded-lg font-medium transition-colors ${
                      activeSettingsTab === 'user-roles'
                        ? 'bg-bengal-green text-white'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    User Roles
                  </button>
                </div>
              </div>

              {/* Custom Fields Builder */}
              {activeSettingsTab === 'custom-fields' && (
                <div className="bg-white rounded-xl shadow-md p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900">Custom Fields Builder</h3>
                    <button
                      onClick={() => setShowFieldForm(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-bengal-green text-white rounded-lg hover:bg-bengal-orange transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Field
                    </button>
                  </div>

                  <div className="space-y-3">
                    {customFields.map((field, index) => (
                      <div key={field.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                        <GripVertical className="w-5 h-5 text-gray-400 cursor-move" />
                        <div className="flex-1">
                          <p className="font-medium text-gray-900">{field.label}</p>
                          <p className="text-sm text-gray-600">{field.type} {field.required && '(Required)'}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => moveField(index, 'up')}
                            disabled={index === 0}
                            className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg disabled:opacity-30"
                          >
                            <ChevronUp className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => moveField(index, 'down')}
                            disabled={index === customFields.length - 1}
                            className="p-2 text-gray-600 hover:bg-gray-200 rounded-lg disabled:opacity-30"
                          >
                            <ChevronDown className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => {
                              setEditingField(field)
                              setShowFieldForm(true)
                            }}
                            className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => deleteCustomField(field.id)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* User Roles Management */}
              {activeSettingsTab === 'user-roles' && (
                <div className="bg-white rounded-xl shadow-md p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-xl font-bold text-gray-900">User Roles Management</h3>
                    <button
                      onClick={() => setShowRoleForm(true)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-bengal-green text-white rounded-lg hover:bg-bengal-orange transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                      Add Role
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {userRoles.map(role => (
                      <div key={role.id} className="border border-gray-200 rounded-lg p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                              {role.name}
                              {role.isDefault && (
                                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">Default</span>
                              )}
                            </h4>
                            <p className="text-sm text-gray-600 mt-1">{role.description}</p>
                          </div>
                          {!role.isDefault && (
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  setEditingRole(role)
                                  setShowRoleForm(true)
                                }}
                                className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => deleteUserRole(role.id)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          )}
                        </div>
                        <div className="space-y-2">
                          {Object.entries(role.permissions).map(([key, value]) => (
                            <div key={key} className="flex items-center gap-2">
                              {value ? (
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              ) : (
                                <XCircle className="w-4 h-4 text-gray-300" />
                              )}
                              <span className="text-sm text-gray-700 capitalize">
                                {key.replace(/([A-Z])/g, ' $1').trim()}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  )
}

export default AdminDashboard